/**
 * 剖面路径可视化插件 —— 将 ProfileService 中的剖面结果渲染为 Three.js 叠加层
 *
 * 职责：
 * 1. 作为 RenderContributor 参与 Overlay3D 阶段渲染
 * 2. 根据 ProfileResult 差分创建/销毁可视化对象（球体、连线、宽度 box、长度标签）
 * 3. 渲染绘制中间状态（currentPoints 预览）
 * 4. 球体按相机距离自动缩放，保持约 15px 屏幕投影大小
 */

import * as THREE from 'three'
import { WEBGL2_ONLY_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import { createTextSprite, disposeTextSprite } from '@/plugins/tools/shared/TextSprite'
import { createLineMaterial, TOOL_COLOR_PROFILE } from '@/plugins/tools/shared/ToolMaterials'
import {
  getActiveViewId,
  getOptionalViewRegistry,
  getViewToolsRoot,
} from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { Plugin } from '@/shared/types/plugin'
import type {
  ProfileService,
  RenderContributor,
  RenderPipeline,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import type { ProfileResult } from './ProfileData'

// ─── 常量 ───

/** 球体几何体半径 */
const SPHERE_RADIUS = 0.4
/** 球体几何体细分 */
const SPHERE_SEGMENTS = 10
/** 球体目标屏幕像素大小 */
const SPHERE_SCREEN_PX = 15
/** 宽度 box 高度（固定较小值） */
const BOX_HEIGHT = 0.3
/** 宽度 box 半透明度 */
const BOX_OPACITY = 0.2

/** 共享球体几何体，避免重复创建 */
const sharedSphereGeometry = new THREE.SphereGeometry(
  SPHERE_RADIUS,
  SPHERE_SEGMENTS,
  SPHERE_SEGMENTS
)

// ─── 辅助函数 ───

/** 格式化长度标签 */
function formatLength(meters: number): string {
  return `${meters.toFixed(2)}m`
}

/** 计算两点的中点 */
function midpoint(a: THREE.Vector3, b: THREE.Vector3): THREE.Vector3 {
  return new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5)
}

/** 安全释放 Group 内所有子对象的 geometry/material/texture */
function disposeGroup(group: THREE.Group): void {
  group.traverse((obj) => {
    if (obj instanceof THREE.Sprite) {
      disposeTextSprite(obj)
      return
    }
    const meshLike = obj as THREE.Mesh | THREE.Line | THREE.Points
    if (meshLike.geometry) {
      meshLike.geometry.dispose()
    }
    if (meshLike.material) {
      const materials = Array.isArray(meshLike.material) ? meshLike.material : [meshLike.material]
      for (const mat of materials) {
        mat.dispose()
      }
    }
  })
  while (group.children.length > 0) {
    group.remove(group.children[0])
  }
}

/** 创建路径点球体 */
function createSphere(): THREE.Mesh {
  const material = new THREE.MeshBasicMaterial({
    color: TOOL_COLOR_PROFILE,
    depthTest: false,
    depthWrite: false,
  })
  return new THREE.Mesh(sharedSphereGeometry, material)
}

/** 创建连接线 */
function createConnectionLine(positions: THREE.Vector3[]): THREE.Line {
  const geometry = new THREE.BufferGeometry()
  geometry.setFromPoints(positions)
  const material = createLineMaterial(TOOL_COLOR_PROFILE)
  return new THREE.Line(geometry, material)
}

/** 创建段间宽度 box */
function createSegmentBox(start: THREE.Vector3, end: THREE.Vector3, width: number): THREE.Mesh {
  const length = start.distanceTo(end)
  const geometry = new THREE.BoxGeometry(length, BOX_HEIGHT, width)
  const material = new THREE.MeshBasicMaterial({
    color: TOOL_COLOR_PROFILE,
    transparent: true,
    opacity: BOX_OPACITY,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
  })
  const mesh = new THREE.Mesh(geometry, material)

  // 位于两点中点
  mesh.position.copy(midpoint(start, end))

  // 沿两点方向旋转
  const direction = new THREE.Vector3().subVectors(end, start).normalize()
  const quaternion = new THREE.Quaternion()
  quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), direction)
  mesh.quaternion.copy(quaternion)

  return mesh
}

/**
 * 根据相机距离计算球体缩放值，使球体在屏幕上保持约 SPHERE_SCREEN_PX 像素
 */
function computeSphereScale(
  spherePos: THREE.Vector3,
  camera: THREE.Camera,
  viewportHeight: number
): number {
  const distance = camera.position.distanceTo(spherePos)
  if (distance === 0) return 1

  if (camera instanceof THREE.PerspectiveCamera) {
    const fovRad = THREE.MathUtils.degToRad(camera.fov)
    // 每像素对应的世界尺寸
    const worldPerPixel = (2 * distance * Math.tan(fovRad / 2)) / viewportHeight
    // 目标世界大小 = 目标像素数 * 每像素世界尺寸
    const targetWorldSize = SPHERE_SCREEN_PX * worldPerPixel
    return targetWorldSize / SPHERE_RADIUS
  }

  if (camera instanceof THREE.OrthographicCamera) {
    const orthoHeight = (camera.top - camera.bottom) / camera.zoom
    const worldPerPixel = orthoHeight / viewportHeight
    const targetWorldSize = SPHERE_SCREEN_PX * worldPerPixel
    return targetWorldSize / SPHERE_RADIUS
  }

  return 1
}

/** 为单个 ProfileResult 构建完整可视化 Group */
function buildProfileVisual(result: ProfileResult): THREE.Group {
  const group = new THREE.Group()
  group.name = `profile-visual-${result.id}`
  const pts = result.points.map((p) => p.position)
  if (pts.length === 0) return group

  // 路径点球体
  for (const pos of pts) {
    const sphere = createSphere()
    sphere.position.copy(pos)
    // 标记为球体，便于 render 阶段缩放
    sphere.userData._isProfileSphere = true
    group.add(sphere)
  }

  // 连接线（2 个点以上）
  if (pts.length >= 2) {
    const line = createConnectionLine(pts)
    group.add(line)
  }

  // 段间宽度 box
  for (let i = 0; i < pts.length - 1; i++) {
    const box = createSegmentBox(pts[i], pts[i + 1], result.width)
    group.add(box)
  }

  // 长度标签（在最后一个点附近）
  if (pts.length >= 2) {
    const label = createTextSprite(formatLength(result.length))
    const lastPt = pts[pts.length - 1]
    label.position.copy(lastPt)
    label.position.y += 0.5
    group.add(label)
  }

  return group
}

// ─── 插件实现 ───

/** 剖面路径可视化插件 */
export class ProfileOverlayPlugin implements Plugin, RenderContributor {
  /** 插件名称 */
  readonly name = 'profile-overlay'
  readonly graphicsRequirements = WEBGL2_ONLY_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 55
  /** 依赖的服务名列表 */
  readonly dependencies = ['ProfileService', 'RenderPipeline'] as const

  // RenderContributor
  /** 渲染贡献者类型标识 */
  readonly kind = 'profile-overlay'
  /** 需要参与的渲染阶段 */
  readonly phases = [RenderPhase.Overlay3D] as const
  /** 渲染优先级 */
  readonly renderPriority = 0

  /** 引擎上下文 */
  private context!: EngineContext
  /** 剖面服务 */
  private profileService!: ProfileService
  /** 渲染管线 */
  private pipeline!: RenderPipeline
  private viewRegistry: ViewRegistry | null = null

  /** 可视化根节点 */
  private readonly root = new THREE.Group()
  /** 临时渲染场景，避免重复渲染 mainScene */
  private readonly overlayScene = new THREE.Scene()
  /** 已渲染的剖面结果缓存 */
  private readonly visualCache = new Map<string, THREE.Group>()
  /** 缓存每个 result 的 width 快照，用于检测宽度变更 */
  private readonly widthSnapshot = new Map<string, number>()
  /** 绘制中间状态预览组 */
  private readonly previewGroup = new THREE.Group()
  /** 上一帧 currentPoints 数量 */
  private lastPreviewPointCount = 0

  constructor() {
    this.root.name = 'profileOverlayRoot'
    this.previewGroup.name = 'profilePreview'
    this.root.add(this.previewGroup)
  }

  // ─── Plugin 生命周期 ───

  /**
   * 初始化：保存上下文。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
  }

  /** 启动：获取服务，注册 RenderContributor，挂载根节点 */
  onStart(): void {
    this.profileService = this.context.getService('ProfileService')
    this.pipeline = this.context.getService('RenderPipeline')
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.pipeline.addRenderContributor(this)
    this.resolveToolsRoot(getActiveViewId(this.viewRegistry)).add(this.root)
  }

  /** 销毁：清理所有 Three.js 对象 */
  onDestroy(): void {
    this.pipeline.removeRenderContributor(this)

    for (const [, group] of this.visualCache) {
      disposeGroup(group)
    }
    this.visualCache.clear()

    disposeGroup(this.previewGroup)
    this.lastPreviewPointCount = 0

    if (this.root.parent) {
      this.root.parent.remove(this.root)
    }
    this.viewRegistry = null
  }

  // ─── RenderContributor ───

  /**
   * 渲染剖面叠加层。
   *
   * @param frame - 帧上下文
   * @param _phase - 当前渲染阶段
   * @param ctx - 当前阶段渲染上下文
   */
  render(frame: ViewFrameContext, _phase: RenderPhase, ctx: PhaseRenderContext): void {
    const activeViewId = getActiveViewId(this.viewRegistry)
    if (activeViewId && ctx.viewId !== activeViewId) {
      return
    }

    const toolsRoot = this.resolveToolsRoot(activeViewId ?? ctx.viewId)
    if (this.root.parent !== toolsRoot) {
      this.root.parent?.remove(this.root)
      toolsRoot.add(this.root)
    }

    // 1. 差分更新已完成的剖面结果
    this.syncResultVisuals()

    // 2. 更新绘制中间状态预览
    this.syncPreview()

    // 3. 自动缩放所有球体
    this.updateSphereScales(frame.camera, ctx.viewport.height)

    // 4. 使用临时场景只渲染自己的 overlay
    if (this.root.children.length === 0) return

    this.overlayScene.children.length = 0
    this.overlayScene.add(this.root)

    submitSceneInView(ctx, this.overlayScene, frame.camera)

    // 渲染后归还 root 到 toolsRoot
    toolsRoot.add(this.root)
  }

  private resolveToolsRoot(viewId: string | null): THREE.Object3D {
    return getViewToolsRoot(this.context, this.viewRegistry, viewId)
  }

  // ─── 私有方法 ───

  /** 差分同步 ProfileService.results → 可视化缓存 */
  private syncResultVisuals(): void {
    const results = this.profileService.getResults()
    const currentIds = new Set<string>()

    for (const result of results) {
      currentIds.add(result.id)
      const cachedWidth = this.widthSnapshot.get(result.id)
      // 宽度变更时重建已有视觉缓存
      if (this.visualCache.has(result.id) && cachedWidth !== result.width) {
        const old = this.visualCache.get(result.id)!
        disposeGroup(old)
        this.root.remove(old)
        this.visualCache.delete(result.id)
      }
      if (!this.visualCache.has(result.id)) {
        const visual = buildProfileVisual(result)
        this.visualCache.set(result.id, visual)
        this.widthSnapshot.set(result.id, result.width)
        this.root.add(visual)
      }
    }

    // 删除已移除的结果
    for (const [id, group] of this.visualCache) {
      if (!currentIds.has(id)) {
        disposeGroup(group)
        this.root.remove(group)
        this.visualCache.delete(id)
        this.widthSnapshot.delete(id)
      }
    }
  }

  /** 同步绘制中间状态预览 */
  private syncPreview(): void {
    const currentPoints = this.profileService.getCurrentPoints()

    if (currentPoints.length === this.lastPreviewPointCount && currentPoints.length === 0) {
      return
    }
    this.lastPreviewPointCount = currentPoints.length

    disposeGroup(this.previewGroup)

    if (currentPoints.length === 0) return

    const positions = currentPoints.map((p) => p.position)

    // 预览球体
    for (const pos of positions) {
      const sphere = createSphere()
      sphere.position.copy(pos)
      sphere.userData._isProfileSphere = true
      this.previewGroup.add(sphere)
    }

    // 两点以上时画连接线
    if (positions.length >= 2) {
      const line = createConnectionLine(positions)
      this.previewGroup.add(line)
    }
  }

  /** 遍历所有球体，根据相机距离动态调整 scale */
  private updateSphereScales(camera: THREE.Camera, viewportHeight: number): void {
    this.root.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.userData._isProfileSphere) {
        const scale = computeSphereScale(obj.position, camera, viewportHeight)
        obj.scale.setScalar(scale)
      }
    })
  }
}
